import {hoistCmp, uses} from '@xh/hoist/core';
import {OrderTicketModel} from './OrderTicketModel';
import {dialog} from '@xh/hoist/kit/blueprint';
import {Icon} from '@xh/hoist/icon';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {div, filler, fragment, hbox, vbox, vspacer} from '@xh/hoist/cmp/layout';
import {button} from '@xh/hoist/desktop/cmp/button';
import {form} from '@xh/hoist/cmp/form';
import {formField} from '@xh/hoist/desktop/cmp/form';
import {numberInput, segmentedControl, select} from '@xh/hoist/desktop/cmp/input';
import './OrderTicket.scss';
import {fmtDateTimeSec, fmtMillions, fmtNumber, fmtPercent, fmtPrice} from '@xh/hoist/format';
import {FullQuote} from '../../../../data/DataTypes';
import {ReactNode} from 'react';
import {securityPicker} from '../../../../cmp/securityPicker/SecurityPicker';
import {secExchangeBadge} from '../../../../cmp/secBadges/SecBadges';

export const orderTicketDialog = hoistCmp.factory({
    className: 'order-ticket-dialog',
    model: uses(OrderTicketModel),
    render({className, model}) {
        return dialog({
            className,
            isOpen: model.isOpen,
            onClose: () => model.close(),
            canOutsideClickClose: false,
            item: panel({
                item: form({
                    model: model.formModel,
                    fieldDefaults: {minimal: true, requiredIndicator: null, commitOnChange: true},
                    item: model.fullQuote?.isListed ? listedTicket() : fixedTicket()
                })
            })
        });
    }
});

const listedTicket = hoistCmp.factory(() => vbox(securityDetailPanel(), orderPanel()));

const fixedTicket = hoistCmp.factory<OrderTicketModel>(({model}) => {
    const quote = model.fullQuote,
        className = 'order-ticket-dialog__data';
    if (!quote) return;

    return hbox({
        items: [
            securityPicker(),
            div({className: 'order-ticket-dialog__data__sep-v'}),
            vbox({
                className: 'order-ticket-dialog__fixed-ticket',
                items: [
                    hbox({
                        alignItems: 'center',
                        items: [div({item: quote.name, className: `${className}__name`})]
                    }),
                    div({className: `${className}__sep-h`}),
                    orderPanel()
                ]
            })
        ]
    });
});

const securityDetailPanel = hoistCmp.factory<OrderTicketModel>(({model}) =>
    hbox(securityPanel(), div({className: 'order-ticket-dialog__data__sep-v'}), dataPanel())
);

const securityPanel = hoistCmp.factory<OrderTicketModel>(({model}) =>
    vbox({
        style: {padding: 4},
        items: [
            vspacer(6),
            securityPicker({
                ticker: model.formModel.values.ticker,
                keepSelection: true,
                showSearchIcon: true,
                autofocus: true,
                placeholder: 'Search security...',
                width: 120,
                onSecuritySelected: sec => model.onSecuritySelect(sec)
            }),
            vspacer(10),
            sellSharesPanel()
        ]
    })
);

const sellSharesPanel = hoistCmp.factory<OrderTicketModel>(({model}) =>
    vbox({
        omit: !model.originalQuantity,
        alignItems: 'center',
        items: [
            `${model.originalQuantity} shares`,
            div({
                className: 'order-ticket-dialog__data__sell-all',
                item: 'sell all',
                onClick: () => model.sellAll()
            })
        ]
    })
);

const dataPanel = hoistCmp.factory<OrderTicketModel>(({model}) => {
    const quote = model.fullQuote,
        className = 'order-ticket-dialog__data';
    if (!quote) return;

    return vbox({
        className,
        items: [
            hbox({
                alignItems: 'center',
                items: [div({item: quote.name, className: `${className}__name`})]
            }),

            div({className: `${className}__sep-h`}),
            hbox(
                vbox({
                    className: `${className}__price-box`,
                    items: [price(model), change(quote), timestamp(quote)]
                }),
                div({className: 'order-ticket-dialog__data__sep-v'}),
                vbox({
                    className: `${className}__price-box`,
                    items: [bid(quote), ask(quote), volume(quote), avgVolume(quote)]
                }),
                div({className: 'order-ticket-dialog__data__sep-v'}),
                vbox({
                    className: `${className}__price-box`,
                    items: [open(quote), close(quote), dayRange(quote), yearRange(quote)]
                })
            )
        ]
    });
});

function item(label: string, value: ReactNode) {
    const className = 'order-ticket-dialog__data';
    return hbox({
        className: `${className}__item-box`,
        items: [div({item: label, className: `${className}__item-label`}), filler(), value]
    });
}

function price(model: OrderTicketModel) {
    if (!model.price) return null;
    const className = 'order-ticket-dialog__data';
    return hbox(
        div({
            item: fmtPrice(model.price, {prefix: '$'}),
            className: `${className}__price`
        }),
        filler(),
        secExchangeBadge(model.fullQuote.exchange)
    );
}

function change(quote: FullQuote) {
    if (!quote.priceChange) return null;
    const className = 'order-ticket-dialog__data';
    return div({
        items: [
            fmtNumber(quote.priceChange, {
                precision: 2
            }),
            ' (',
            fmtPercent(quote.priceChangePct),
            ')'
        ],
        className: `${className}__change ${quote.priceChange < 0 ? 'neg' : 'pos'}`
    });
}

function timestamp(quote: FullQuote) {
    const className = 'order-ticket-dialog__data';
    return div({className: `${className}__timestamp`, item: fmtDateTimeSec(quote.timestamp)});
}

function volume(quote: FullQuote) {
    return item('Volume', fmtMillions(quote.volume, {precision: 2, label: 'M'}));
}

function avgVolume(quote: FullQuote) {
    return item('Avg Volume', fmtMillions(quote.avgVolume, {precision: 2, label: 'M'}));
}

function open(quote: FullQuote) {
    return item('Open', div({item: quote.open}));
}

function close(quote: FullQuote) {
    return item('Close', div({item: quote.prevClose}));
}

function bid(quote: FullQuote) {
    return item(
        'Bid/Size',
        fragment(
            div({item: quote.bidSize}),
            'x ',
            div({
                className: 'order-ticket-dialog__data__bidask',
                item: fmtNumber(quote.bid, {precision: 2})
            })
        )
    );
}

function ask(quote: FullQuote) {
    return item(
        'Ask/Size',
        fragment(
            div({item: quote.askSize}),
            'x ',
            div({
                className: 'order-ticket-dialog__data__bidask',
                item: fmtNumber(quote.ask, {precision: 2})
            })
        )
    );
}

function dayRange(quote: FullQuote) {
    return item('Day Range', div(fmtNumber(quote.dayLow, {precision: 2}), ' - ', fmtNumber(quote.dayHigh, {precision: 2})));
}

function yearRange(quote: FullQuote) {
    return item('52 Week', div(fmtNumber(quote.yearLow, {precision: 2}), ' - ', fmtNumber(quote.yearHigh, {precision: 2})));
}

const orderPanel = hoistCmp.factory<OrderTicketModel>(({model}) =>
    hbox({
        className: 'order-ticket-dialog__order',
        items: [
            formField({
                field: 'action',
                item: segmentedControl({
                    options: [
                        {value: 'buy', label: model.fullQuote?.isCash ? 'Deposit' : 'Buy', intent: 'success'},
                        {value: 'sell', label: model.fullQuote?.isCash ? 'Withdraw' : 'Sell', intent: 'danger'}
                    ]
                })
            }),
            formField({field: 'quantity', item: numberInput({width: 120, placeholder: '0'})}),
            formField({
                field: 'orderType',
                item: select({width: 120, options: model.orderTypes}),
                omit: !model.fullQuote.isListed
            }),
            formField({
                field: 'limitPrice',
                item: numberInput({width: 100, placeholder: '0'}),
                omit: !model.fullQuote.isListed
            }),
            formField({
                field: 'timing',
                item: segmentedControl({
                    options: [
                        {value: 'day', label: 'Day'},
                        {value: 'gtc', label: 'GTC'}
                    ]
                }),
                omit: !model.fullQuote.isListed
            }),
            formField({
                field: 'estAmount',
                item: numberInput({
                    width: 140,
                    style: {fontSize: 'larger'},
                    displayWithCommas: true,
                    precision: 2,
                    leftIcon: Icon.dollarSign()
                })
            }),
            button({
                text: 'Place',
                icon: Icon.book(),
                intent: 'primary',
                minimal: false,
                className: 'order-ticket-dialog__book',
                disabled: !model.formModel.isValid,
                onClick: () => model.place()
            })
        ]
    })
);
